/**
 * Dynamic-import recovery for route/shell chunks and feature libraries.
 *
 * Why this exists: React.lazy memoizes a REJECTED import promise for the
 * whole page lifetime, so one failed chunk fetch (a 2-second network blip —
 * the chunk itself was still 200-OK on the server) leaves the page
 * permanently dead: the ErrorBoundary's "Try Again" re-renders the same
 * rejection and can never recover. Observed in prod 2026-08-26
 * (SettingsPage-BxmI_Gu2.js, session 01a03b54…, 91 console errors).
 *
 * Why retries cache-bust instead of re-calling the importer: Chromium
 * memoizes a FAILED module fetch in the document's module map, so re-running
 * the same import() rejects instantly without touching the network (verified
 * via Playwright: the retry produced no second request). Chrome and Firefox
 * put the chunk URL in the error message, so the retry imports that URL with
 * a hw_retry query param — a genuinely fresh fetch. Browsers that omit the
 * URL (Safari's "Importing a module script failed.") re-call the importer —
 * useful where the failure wasn't cached — and otherwise fall through.
 *
 * Recovery ladder for lazyWithRetry (route/shell chunks):
 *   1. cache-busted re-fetch after 300ms, then 1200ms (a transient blip
 *      recovers IN PLACE — no reload, no lost page state);
 *   2. one guarded full page reload per 30s window (the stale-deploy case,
 *      where only fresh index.html can fix the URL, and the Safari case);
 *   3. throw to the ErrorBoundary (real outage — its "Reload Page" button
 *      is the manual path).
 *
 * retryImport (feature libs, e.g. jspdf) stops after step 1 and throws:
 * reloading the whole page because an optional feature's chunk failed would
 * destroy the member's context mid-action.
 *
 * This module OWNS chunk recovery: the old vite:preloadError listener in
 * index.jsx reloaded on the FIRST failure, which both skipped the graceful
 * retry and spent the reload budget before it could matter.
 */
import React from 'react';
import { track } from './analytics';

const RELOAD_FLAG = 'heyward_chunk_reload_at';
const RELOAD_BUDGET_MS = 30_000;
const RETRY_DELAYS_MS = [300, 1200];

// Fallback guard for when sessionStorage is unavailable (Safari private
// mode, storage-blocked browsers) — without it those users would reload-loop.
let memoryReloadAt = 0;

function lastReloadAt() {
  try {
    return Number(sessionStorage.getItem(RELOAD_FLAG) || 0) || memoryReloadAt;
  } catch {
    // Storage unavailable — the in-memory mark still prevents a loop within
    // this page lifetime, which is the only place a loop can happen.
    return memoryReloadAt;
  }
}

function markReload(now) {
  memoryReloadAt = now;
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(now));
  } catch {
    // Storage unavailable — memoryReloadAt above already holds the mark.
  }
}

// At most one automatic page reload per 30s window. Returns true when it
// initiated the reload.
function attemptGuardedReload(source) {
  const now = Date.now();
  if (now - lastReloadAt() < RELOAD_BUDGET_MS) return false;
  markReload(now);
  track.chunkReloadTriggered(source);
  window.location.reload();
  return true;
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Chrome: "Failed to fetch dynamically imported module: https://…/x.js"
// Firefox: "error loading dynamically imported module: https://…/x.js"
// Safari gives no URL — returns null there.
function failedImportUrl(error) {
  const match = /(https?:\/\/\S+)/.exec(error?.message || '');
  if (!match) return null;
  return match[1].replace(/[)'",.]+$/, '');
}

// Vite's preload helper throws exactly this when a chunk's stylesheet fails
// (three chunks ship CSS: landing, LandingFilmPage, PropertySearch). It also
// marks the URL as attempted, so re-running the importer would silently SKIP
// the stylesheet — the page would come up unstyled. The retry has to inject
// a fresh cache-busted <link> itself before re-importing.
const CSS_PRELOAD_MSG = /^Unable to preload CSS for (\S+)/;

function retryCssPreload(url, attempt) {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `${url}${url.includes('?') ? '&' : '?'}hw_retry=${attempt}`;
    link.addEventListener('load', () => resolve());
    link.addEventListener('error', () => {
      link.remove();
      reject(new Error(`Unable to preload CSS for ${url}`));
    });
    document.head.appendChild(link);
  });
}

async function importWithRetries(importer, chunkLabel) {
  let lastError;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      let ns;
      const cssMatch = attempt > 0 ? CSS_PRELOAD_MSG.exec(lastError?.message || '') : null;
      const url = !cssMatch && attempt > 0 ? failedImportUrl(lastError) : null;
      if (cssMatch) {
        await retryCssPreload(cssMatch[1], attempt);
        ns = await importer();
      } else if (url) {
        const busted = `${url}${url.includes('?') ? '&' : '?'}hw_retry=${attempt}`;
        ns = await import(/* @vite-ignore */ busted);
      } else {
        ns = await importer();
      }
      if (attempt > 0) track.chunkRetrySucceeded(chunkLabel, attempt);
      return ns;
    } catch (error) {
      lastError = error;
      if (attempt < RETRY_DELAYS_MS.length) await wait(RETRY_DELAYS_MS[attempt]);
    }
  }
  throw lastError;
}

/**
 * Retry a feature-library import (300ms, 1200ms) and then throw — no page
 * reload. For optional features whose failure should surface where the
 * member acted, not blow away their page state. Pass a RAW importer
 * (`() => import('lib')`) and map the namespace on the returned promise.
 */
export function retryImport(importer, chunkLabel) {
  return importWithRetries(importer, chunkLabel);
}

/**
 * The async loader behind lazyWithRetry, exported separately so the retry
 * contract is unit-testable without rendering through React.lazy.
 */
export function createRetryingLoader(importer, chunkLabel, select) {
  return async function retryingLoader() {
    try {
      const ns = await importWithRetries(importer, chunkLabel);
      return select ? select(ns) : ns;
    } catch (error) {
      if (attemptGuardedReload(chunkLabel)) {
        // The page is reloading: stay pending so Suspense keeps its fallback
        // up instead of flashing the error boundary during unload.
        return new Promise(() => {});
      }
      throw error;
    }
  };
}

/**
 * React.lazy with the recovery ladder. `importer` must be a RAW
 * `() => import('…')` (no .then chains — a cache-busted retry re-imports by
 * URL and would skip them); use `select` to pick a non-default export,
 * e.g. lazyWithRetry(() => import('./x'), 'X', (m) => ({ default: m.X })).
 */
export function lazyWithRetry(importer, chunkLabel, select) {
  return React.lazy(createRetryingLoader(importer, chunkLabel, select));
}
